import { zodResolver } from "@hookform/resolvers/zod";
import axios, { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Button } from "src/components/buttons/shadcn-button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/dialogue/radix-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "src/components/form/form";
import { Input } from "src/components/input/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";
import { grantAccess } from "src/redux/api/_hr.api";
import { GrantAccessSchema } from "src/schemas/grant-access-employee";
import { z } from "zod";

interface GrantAccessDialogProps {
  children: React.ReactNode;
  full_name: string;
  id: number;
}

const GrantAccessDialog = ({
  full_name,
  id,
  children,
}: GrantAccessDialogProps) => {
  const Roles = ["RWO", "AWO", "DWO", "ITWO", "HRWO", "SO", "MD"];

  const form = useForm<z.infer<typeof GrantAccessSchema>>({
    resolver: zodResolver(GrantAccessSchema),
  });

  const grantAccessToEmployees = async (
    values: z.infer<typeof GrantAccessSchema>
  ) => {
    try {
      const response = await grantAccess(id, values);

      if (response.message.toLowerCase() === "success") {
        toast.success("Access Granted to Employee!");
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;

        if (axiosError.response) {
          toast.warning(error.response?.data.data.message);
        }
      }
      toast.error("Failed to Grant Access to Employee!");
      console.error(error);
    }
  };

  return (
    <Dialog>
      <DialogTrigger>{children}</DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="text-center">
            Grant Access to{" "}
            <span className="text-xl uppercase font-medium text-primary">
              {full_name}
            </span>
          </DialogTitle>
          <DialogDescription className="font-semibold text-red-500 text-center">
            This action is irreversible.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(grantAccessToEmployees)}>
            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="role"
                defaultValue={undefined}
                render={({ field }) => (
                  <FormItem>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select the Role of the Employee" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Roles.map((role) => {
                          return (
                            <SelectItem
                              key={role}
                              value={role}
                              className="focus:bg-accent focus:text-foreground"
                            >
                              {role}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Enter the Access Password"
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="text-gray-500 text-sm flex flex-wrap gap-x-3">
                <p>Minimum 2 Lower Case |</p>
                <p>Minimum 2 Upper Case |</p>
                <p>Minimum 2 Numbers |</p>
                <p>Minimum 2 Symbols</p>
              </div>
            </div>
            <div className="flex justify-between space-y-1 my-4">
              <Button
                className="bg-green-50 hover:bg-green-200 text-green-700 h-9 px-4 py-2 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                type="submit"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? (
                  <span className="loading loading-dots loading-md"></span>
                ) : (
                  "Grant Access"
                )}
              </Button>
              <DialogClose>
                <Button
                  className="bg-red-50 hover:bg-red-200 text-red-700 h-9 px-4 py-2 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                  type="button"
                  disabled={form.formState.isSubmitting}
                >
                  Cancel
                </Button>
              </DialogClose>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default GrantAccessDialog;
